import { ShieldCheck } from 'lucide-react'
import type { ReactNode } from 'react'
import { useMfaStatus } from '@/auth/useMfaStatus'
import { Skeleton } from '@/components/ui/skeleton'
import { MfaEnrolment } from '@/features/mfa/MfaEnrolment'
import { MfaStepUpDialog } from '@/features/mfa/MfaStepUpDialog'

/**
 * Founder-portal MFA gate (spec-36 §5.1). Hoisted from the old
 * FounderFinancialDdPage: TOTP is now mandatory for the entire founder
 * environment (not just financial-DD), matching the backend's aal2 guard on
 * the founder router.
 *
 * Behaviour:
 * - `mfa.loading` → skeleton placeholder.
 * - No verified factor → mandatory inline enrolment (blocks children).
 * - Verified factor but session at aal1 → step-up dialog (blocks children).
 * - aal2 → render `children` unchanged.
 *
 * Founder-facing copy is English. The reused `MfaEnrolment` component retains
 * its shared Dutch strings (spec-14b), which is the same trade-off the
 * previous financial-DD gate made.
 */
export function FounderMfaGate({ children }: { children: ReactNode }) {
  const mfa = useMfaStatus()

  if (mfa.loading) {
    return (
      <section className="mx-auto flex max-w-2xl flex-col gap-4" aria-hidden>
        <Skeleton className="h-8 w-64" />
        <div className="vonk-card flex flex-col gap-3">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </section>
    )
  }

  if (mfa.currentLevel === 'aal2') {
    return <>{children}</>
  }

  if (!mfa.hasVerifiedFactor) {
    return (
      <section className="mx-auto flex max-w-2xl flex-col gap-4">
        <GateHeading title="Secure your account" />
        <div className="vonk-card flex flex-col gap-3">
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <ShieldCheck className="size-4 shrink-0 text-primary" aria-hidden />
            Two-factor authentication is required for the founder portal. Please enable your
            authenticator app to continue.
          </p>
          <MfaEnrolment onVerified={() => mfa.refresh()} />
        </div>
      </section>
    )
  }

  // Verified factor but session still aal1 — step-up required.
  return (
    <section className="mx-auto flex max-w-2xl flex-col gap-4">
      <GateHeading title="Verify it's you" />
      <div className="vonk-card">
        <p className="text-sm text-muted-foreground">
          The founder portal requires two-factor verification. Enter your authenticator code to
          continue.
        </p>
      </div>
      <MfaStepUpDialog
        open
        onElevated={() => mfa.refresh()}
        onCancel={() => mfa.refresh()}
      />
    </section>
  )
}

function GateHeading({ title }: { title: string }) {
  return (
    <div>
      <h1 className="font-heading text-2xl font-semibold text-white">{title}</h1>
    </div>
  )
}
